import React, { FC } from 'react'

import clsx from 'clsx'
import {
  Control,
  Controller,
  UseFieldArrayRemove,
  UseFormRegister,
  UseFormWatch,
} from 'react-hook-form'
import { UseFormSetValue, UseFormTrigger } from 'react-hook-form/dist/types/form'
import Select from 'react-select'

import MessageHelperComp from '../../../../../helpers/MessageHelper'
import CustomDatePicker from '../../../../common/inputs/CustomDatePicker'
import Input from '../../../../common/inputs/Input'
import GlobalInputComponent from '../../detail/Components/GlobalInputComponent'
import { IconCheck } from '../../../../icons/IconCheck'

import { getReactPickerDate } from '../../../../../models/interface.helper'
import { cn } from '../../../../../lib/utils'

import { MODAL_CONSTS } from '../../../../../const/modal-const'
import {
  HOURS_12_OPTIONS,
  MINUTES_OPTIONS,
  PERIODS_OPTIONS,
  TAG_OPTIONS,
} from '../../../../../fakeData/TimeList'
import { IRegistrationByDayInterface } from '../../../../../models/registrations-by-day/registrationByDay.interface'

interface ScheduleItemByClassProps {
  index: number
  hasChildren?: boolean
  handleModal: (showHide: boolean, typeOfModal: string, dataToPassOn?: any) => void
  register: UseFormRegister<any>
  trigger: UseFormTrigger<any>
  control: Control<any>
  watch: UseFormWatch<any>
  setValue: UseFormSetValue<any>
  remove?: UseFieldArrayRemove
  areAllFieldsValid: (index: number) => boolean
  locationOptions: { label: string; value: string }[]
  parentSelector?: `registrationFees.${number}`
  selector:
    | `custom.${number}`
    | `registrationFees.${number}`
    | `registrationFees.${number}.children.${number}`
  errorSelector: { [key: string]: { message: string } }
  selectedIds: string[]
  onSelected: (id: string) => void
  isBatchSchedule: boolean
  registrationByDay: IRegistrationByDayInterface[]
}

export const ScheduleItemByClass: FC<ScheduleItemByClassProps> = ({
  index,
  handleModal,
  register,
  trigger,
  control,
  watch,
  setValue,
  areAllFieldsValid,
  locationOptions,
  remove,
  selector,
  parentSelector,
  hasChildren,
  errorSelector,
  selectedIds,
  onSelected,
  isBatchSchedule,
  registrationByDay,
}) => {
  const openAddNotesModal = (noteInputRef: any) => {
    handleModal(true, MODAL_CONSTS.ADD_NOTE, {
      noteInputRef,
      setValue: setValue,
      trigger,
    })
  }

  const countRiders = registrationByDay.filter(
    (rider) => rider.uuid === watch(`${selector}.uuid`)
  ).length

  return (
    <div
      className={clsx(
        'min-h-[56px] mt-4 w-full relative border border-SeabiscuitLightThemeColorD3 rounded-lg flex flex-wrap gap-y-2 items-center justify-between px-4 py-2',
        areAllFieldsValid(index)
          ? 'bg-SeabiscuitGrayThemeColor'
          : 'bg-white border-SeabiscuitLightThemeColorD3'
      )}
    >
      {/* Name Field */}
      <div className="relative w-full sm:w-[48%] lg:w-[21%]">
        <Input
          placeholder="Name custom time"
          register={register}
          name={`${selector}.name`}
          value={watch(`${selector}.name`)}
          className="p-0 border-none bg-transparent"
        />
        {!!errorSelector?.name?.message ? (
          <MessageHelperComp
            className="absolute z-0 bottom-0 left-0 translate-y-full"
            isError={true}
            message={errorSelector?.name?.message}
          />
        ) : null}
      </div>

      {!hasChildren && (
        <>
          {/* Location Field */}
          <div className="relative w-full sm:w-[48%] lg:w-[15%]">
            <Controller
              control={control}
              name={`${selector}.location`}
              render={() => {
                return (
                  <Select
                    isDisabled={locationOptions?.[0]?.label?.length <= 0}
                    className="p-0 bg-transparent border-none transition-all hover:opacity-70"
                    classNamePrefix="entries-select"
                    isClearable={false}
                    isSearchable={true}
                    placeholder="Facility"
                    value={
                      watch(`${selector}.location`)
                        ? {
                            label: watch(`${selector}.location`),
                            value: watch(`${selector}.location`),
                          }
                        : null
                    }
                    options={locationOptions}
                    onChange={(newValue) => {
                      if (newValue) {
                        setValue(`${selector}.location`, newValue.value)
                        if (parentSelector && index === 0)
                          setValue(`${parentSelector}.location`, newValue.value)
                      }
                    }}
                    menuPlacement="auto"
                    menuPortalTarget={document.body}
                  />
                )
              }}
            />
            {!!errorSelector?.location?.message ? (
              <MessageHelperComp
                className="absolute z-0 bottom-0 left-0 translate-y-full"
                isError={true}
                message={errorSelector?.location?.message}
              />
            ) : null}
          </div>

          {/* Start Date Field */}
          <div className="relative w-full sm:w-[48%] lg:w-[13%]">
            <Controller
              name={`${selector}.startDate`}
              control={control}
              render={({ field: { onChange, onBlur } }) => {
                return (
                  <CustomDatePicker
                    onChange={(date) => {
                      onChange(date)
                      if (parentSelector && index === 0)
                        setValue(`${parentSelector}.startDate`, date)
                    }}
                    onBlur={onBlur}
                    targetId="fieldContainer"
                    selected={getReactPickerDate(watch(`${selector}.startDate`))}
                    peekNextMonth
                    dropdownMode="select"
                    showMonthDropdown
                    minDate={new Date()}
                    showYearDropdown
                    placeholderText="Day"
                    className="p-0 border-none bg-transparent"
                    dateFormat="EEEE"
                    showArrow
                  />
                )
              }}
            />
            {!!errorSelector?.startDate?.message ? (
              <MessageHelperComp
                className="absolute z-0 bottom-0 left-0 translate-y-full"
                isError={true}
                message={errorSelector?.startDate?.message}
              />
            ) : null}
          </div>

          {/* Time Selectors */}
          <div className="relative w-full sm:w-[48%] lg:w-[12%]">
            <div className="min-h-[38px] flex items-center text-base start-time-wrapper max-w-[80px]">
              <Controller
                name={`${selector}.startTimeHours`}
                control={control}
                render={({ field }) => (
                  <GlobalInputComponent
                    className="flex-1 !p-0 flex items-center border-none outline-0 text-gray-500 w-full focus:ring-0 focus:border-0 focus:outline-0 bg-transparent"
                    placeholder="00"
                    type="select"
                    value={HOURS_12_OPTIONS}
                    control={control}
                    selectStyleTransparent={true}
                    onChange={(newValue) => {
                      field.onChange(newValue)
                      if (parentSelector && index === 0)
                        setValue(`${parentSelector}.startTimeHours`, newValue)
                    }}
                    isTimeSelector
                    isdisableInput
                    inputName={field.name}
                  />
                )}
              />
              :
              <Controller
                name={`${selector}.startTimeMinutes`}
                control={control}
                render={({ field }) => (
                  <GlobalInputComponent
                    className="flex-1 !p-0 flex items-center border-none outline-0 text-gray-500 w-full focus:ring-0 focus:border-0 focus:outline-0 bg-transparent"
                    placeholder="00"
                    type="select"
                    selectStyleTransparent={true}
                    value={MINUTES_OPTIONS}
                    isTimeSelector
                    onChange={(newValue) => {
                      field.onChange(newValue)
                      if (parentSelector && index === 0)
                        setValue(`${parentSelector}.startTimeMinutes`, newValue)
                    }}
                    isdisableInput
                    control={control}
                    inputName={field.name}
                  />
                )}
              />
              <Controller
                name={`${selector}.startTimeFormat`}
                control={control}
                render={({ field }) => (
                  <GlobalInputComponent
                    className="flex-1 !p-0 flex items-center border-none outline-0 text-gray-500 w-full focus:ring-0 focus:border-0 focus:outline-0 bg-transparent"
                    placeholder="AM"
                    type="select"
                    value={PERIODS_OPTIONS}
                    selectStyleTransparent={true}
                    isTimeSelector
                    onChange={(newValue) => {
                      field.onChange(newValue)
                      if (parentSelector && index === 0)
                        setValue(`${parentSelector}.startTimeFormat`, newValue)
                    }}
                    isdisableInput
                    control={control}
                    inputName={field.name}
                  />
                )}
              />
            </div>
          </div>

          {/* Tag Field */}
          <div className="relative w-full sm:w-[48%] lg:w-[12%]">
            <Controller
              name={`${selector}.tag`}
              control={control}
              render={() => {
                return (
                  <Select
                    classNamePrefix="entries-select"
                    isClearable={false}
                    isSearchable={true}
                    menuPlacement="auto"
                    placeholder="Tag"
                    value={
                      TAG_OPTIONS.find((tag) => tag.value === watch(`${selector}.tag`)) || null
                    }
                    options={TAG_OPTIONS}
                    className="p-0 bg-transparent border-none transition-all hover:opacity-70 capitalize"
                    menuPortalTarget={document.body}
                    onChange={(newValue) => {
                      if (newValue) {
                        setValue(`${selector}.tag`, newValue.value)
                        if (parentSelector && index === 0)
                          setValue(`${parentSelector}.tag`, newValue.value)
                      }
                    }}
                  />
                )
              }}
            />
            {!!errorSelector?.tag?.message ? (
              <MessageHelperComp
                className="absolute z-0 bottom-0 left-0 translate-y-full"
                isError={true}
                message={errorSelector?.tag?.message}
              />
            ) : null}
          </div>

          {/* Note Field */}
          <div className="relative w-full sm:w-[48%] lg:w-[13%]">
            <label className="text-[14px] flex items-center">
              <Input
                readOnly
                placeholder="Enter note"
                register={register}
                name={`${selector}.note`}
                onClick={(e) => openAddNotesModal(e.target)}
                value={watch(`${selector}.note`)}
                className="p-0 underline bg-transparent border-none text-[14px] text-SeabiscuitDark200ThemeColor w-full rounded-md flex items-center cursor-pointer hover:no-underline overflow-hidden truncate"
              />
            </label>
          </div>

          {/* Entries Field */}
          <div
            className={cn(
              'relative w-full sm:w-[48%] lg:w-[9%] text-[14px]',
              !countRiders && 'opacity-50'
            )}
          >
            {countRiders} entries
          </div>

          {/* Checkbox Field */}
          {isBatchSchedule && (
            <button
              type="button"
              className={cn(
                'border border-solid w-[20px] h-[20px] text-[#00B6AA] rounded-full flex items-center justify-center',
                selectedIds.includes(watch(`${selector}.uuid`))
                  ? 'border-[#00B6AA]'
                  : 'border-[#D3DAEE]'
              )}
              onClick={() => onSelected(watch(`${selector}.uuid`))}
            >
              {selectedIds.includes(watch(`${selector}.uuid`)) && <IconCheck />}
            </button>
          )}

          {/* Remove Button */}
          {remove && (
            <button
              className={clsx(
                'cursor-pointer absolute top-4 -right-1 translate-x-full flex items-center w-6'
              )}
              onClick={() => {
                handleModal(true, MODAL_CONSTS.REMOVE_CONFIRMATION, {
                  removeItem: function () {
                    remove(index)
                  },
                })
              }}
            >
              <img src="/assets/og_icons/Cancel.svg" className="w-full" alt="remove" />
            </button>
          )}
        </>
      )}
    </div>
  )
}
